/**
 * Rectangular "zoom lens" magnifier.
 * Shows a vertical rectangular window at the cursor that reveals a zoomed
 * region of the product image.
 *
 * Desktop-only. The PDP loader avoids downloading this file unless the
 * primary input supports both hover and a fine pointer; CSS retains a
 * defensive touch-device guard for convertible-device edge cases.
 */
(function () {
  /* Best-practice loupe sizing: modest enough not to obscure the product
     (Baymard - a lens covering >~40% of the photo hurts UX), big enough to
     be useful. ~150×190 ≈ 35–44% of a 433px image. Zoom 2× with the
     hi-res source below stays sharp without feeling disorienting. */
  const LENS_WIDTH = 150;
  const LENS_HEIGHT = 190;
  const ZOOM_RATIO = 2;

  /* The lens magnifies, so it needs a higher-res source than the small
     displayed image or the zoom looks soft. Request ~1500px (masters are
     2000px) by bumping the Shopify width param. */
  function hiResSrc(image) {
    var src = image.currentSrc || image.src || '';
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, '$11500');
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=1500';
  }

  function createLens(image) {
    const lens = document.createElement('div');
    lens.className = 'image-magnify-lens';
    lens.setAttribute('aria-hidden', 'true');
    lens.style.backgroundImage = `url('${hiResSrc(image)}')`;
    lens.style.backgroundRepeat = 'no-repeat';
    lens.style.width = `${LENS_WIDTH}px`;
    lens.style.height = `${LENS_HEIGHT}px`;
    // Position inside the image's positioned container
    if (!image.parentElement) return null;
    image.parentElement.appendChild(lens);
    return lens;
  }

  function updateLens(image, lens, event) {
    const rect = image.getBoundingClientRect();
    // Cursor position inside the image, clamped to its bounds.
    const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height));

    // Lens BOX is clamped so it never sticks out past the photo edges.
    const lx = Math.max(LENS_WIDTH / 2, Math.min(x, rect.width - LENS_WIDTH / 2));
    const ly = Math.max(LENS_HEIGHT / 2, Math.min(y, rect.height - LENS_HEIGHT / 2));
    // GPU-composited move (see CSS note) - no per-frame layout/shadow repaint.
    lens.style.transform = `translate3d(${lx - LENS_WIDTH / 2}px, ${ly - LENS_HEIGHT / 2}px, 0)`;

    // Background PANS across the full zoomed image by the cursor's fraction
    // across the photo. Using the clamped lens center here (the old bug)
    // meant the cursor could never address the outer ~LENS/2 band, so the
    // corners/edges of the photo were never reachable - the zoom looked
    // stuck near the middle. Mapping fraction → (bgSize − lens) instead
    // lets x=0 show the left edge and x=width show the right edge.
    const bgW = rect.width * ZOOM_RATIO;
    const bgH = rect.height * ZOOM_RATIO;
    const maxX = Math.max(0, bgW - LENS_WIDTH);
    const maxY = Math.max(0, bgH - LENS_HEIGHT);
    const bgX = (rect.width  ? x / rect.width  : 0) * maxX;
    const bgY = (rect.height ? y / rect.height : 0) * maxY;
    lens.style.backgroundSize = `${bgW}px ${bgH}px`;
    lens.style.backgroundPosition = `-${bgX}px -${bgY}px`;
  }

  /* ─── Overlay hiding ──────────────────────────────────────────────
     The lens shows a zoomed copy of the IMG only. The HTML overlays that
     float on top of the image - the Lottie "NEW" badge
     (.product-badge--new) and the FOMO chip (.fomo-rotator) - are not part
     of the image, so over the zoomed view they'd just sit there covering
     the product and looking out of place. While the lens is active we fade
     them out, and restore them when it closes. */
  const OVERLAY_SELECTORS = '.fomo-rotator';
  function getOverlaysNear(image) {
    /* Limit search to overlays in the same gallery - avoid scaling
       unrelated badges elsewhere on the page. Walk up to the WIDEST
       gallery wrapper (slider-component / media-gallery) so we catch
       FOMO chips that sit as siblings of .product-media-container. */
    const root =
      image.closest('media-gallery') ||
      image.closest('slider-component') ||
      image.closest('.product-media-container') ||
      document;
    return root.querySelectorAll(OVERLAY_SELECTORS);
  }
  function hideOverlays(image) {
    if (!image.__overlaysCache) {
      image.__overlaysCache = Array.from(getOverlaysNear(image));
    }
    image.__overlaysCache.forEach((el) => {
      // `visibility` is the reliable lever here. The FOMO chip runs a CSS
      // opacity animation (`fomoFadeIn`) that overrides any inline opacity,
      // so opacity:0 alone is ignored - and disabling its animation breaks
      // the restore. `visibility:hidden` hides it regardless of opacity and
      // restores cleanly. Opacity still gives the badge a soft fade-out;
      // the delayed visibility lets that fade finish before it's hidden.
      el.style.transition = 'opacity 0.15s ease, visibility 0s linear 0.15s';
      el.style.opacity = '0';
      el.style.visibility = 'hidden';
      el.style.pointerEvents = 'none';
    });
  }
  function showOverlays(image) {
    if (!image.__overlaysCache) return;
    image.__overlaysCache.forEach((el) => {
      // visibility back on immediately; opacity fades back in.
      el.style.transition = 'opacity 0.15s ease';
      el.style.opacity = '';
      el.style.visibility = '';
      el.style.pointerEvents = '';
      setTimeout(() => { el.style.transition = ''; }, 200);
    });
  }

  function attachLens(image) {
    if (image.__magnifyAttached) return;
    image.__magnifyAttached = true;

    let lens = null;
    let rafPending = false;
    let lastEvent = null;

    const onEnter = (event) => {
      if (lens) return;
      lens = createLens(image);
      if (!lens) return;
      updateLens(image, lens, event);
      hideOverlays(image);
    };
    const onMove = (event) => {
      lastEvent = event; // always pan to the LATEST cursor position, not the first of the frame
      if (!lens || rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        if (lens && lastEvent) updateLens(image, lens, lastEvent);
        rafPending = false;
      });
    };
    const onLeave = () => {
      if (!lens) return;
      lens.remove();
      lens = null;
      showOverlays(image);
    };

    // Track via AbortController so callers can revoke later (e.g. when the product gallery is re-rendered after a variant swap).
    const abort = new AbortController();
    const signal = abort.signal;
    image.addEventListener('mouseenter', onEnter, { signal });
    image.addEventListener('mousemove', onMove, { signal });
    image.addEventListener('mouseleave', onLeave, { signal });
    image.__magnifyAbort = abort;
  }

  // Public detach helper - useful when the gallery re-renders the image element.
  window.detachMagnify = function (image) {
    if (image && image.__magnifyAbort) {
      try { image.__magnifyAbort.abort(); } catch (_) {}
      image.__magnifyAbort = null;
      image.__magnifyAttached = false;
    }
  };

  function enableZoomOnHover() {
    const selector = '.image-magnify-hover, .image-magnify-lightbox, .pswp-product-image';
    document.querySelectorAll(selector).forEach(attachLens);
  }

  // Fine-pointer devices only - lens is irrelevant on touch. CSS handles
  // the rest of the gating (viewport width / `pointer: coarse`).
  if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', enableZoomOnHover);
    } else {
      enableZoomOnHover();
    }

    // Re-attach after an AJAX option swap. linked-options-ajax.js replaces
    // the gallery with fresh <img> elements that have no lens handlers, so
    // without this the magnifier stops working once you change a linked
    // option (colour / storage / version). enableZoomOnHover is idempotent -
    // the __magnifyAttached guard skips already-bound images - so it only
    // wires up the new ones. The 100ms delay (same as the autoplay re-init)
    // lets the swapped section settle in the DOM first.
    document.addEventListener('linkedOptionsLoaded', function () {
      setTimeout(enableZoomOnHover, 100);
    });
  }
})();
